"use client";

import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowDownLeftIcon,
  ArrowUpRightIcon,
  MinusIcon,
  SearchIcon,
  ShieldIcon,
  UserIcon,
} from "lucide-react";
import { memo, useState } from "react";
import { promptGenerators } from "@/hooks/use-quick-action";
import { cn } from "@/lib/utils";
import { truncateAddress } from "./address-display";
import { ChainBadge, TransactionStatusBadge } from "./chain-badge";
import { InlineActions } from "./quick-actions-bar";
import type {
  QuickAction,
  QuickActionHandler,
  TransactionDirection,
  TransactionSummary,
} from "./types";
import { getChainInfo } from "./types";
import { MethodBadge, TimeAgo, ValueDisplay } from "./value-display";

export type TransactionRowProps = {
  /** Transaction data */
  transaction: TransactionSummary;
  /** Handler for quick actions */
  onQuickAction: QuickActionHandler;
  /** The user's address to determine direction */
  userAddress?: string;
  /** Whether to show the chain badge */
  showChain?: boolean;
  /** Whether to enable hover actions */
  enableHoverActions?: boolean;
  /** Size variant */
  size?: "sm" | "md" | "lg";
  /** Additional CSS classes */
  className?: string;
  /** Click handler for the row */
  onClick?: () => void;
};

/**
 * Determine transaction direction relative to user address
 */
function getDirection(
  tx: TransactionSummary,
  userAddress?: string
): TransactionDirection {
  if (!userAddress) {
    return "outgoing";
  }
  const normalizedUser = userAddress.toLowerCase();
  const from = tx.from.toLowerCase();
  const to = tx.to?.toLowerCase();

  if (from === normalizedUser && to === normalizedUser) {
    return "self";
  }
  if (from === normalizedUser) {
    return "outgoing";
  }
  if (to === normalizedUser) {
    return "incoming";
  }
  return "outgoing";
}

const directionConfig = {
  incoming: {
    icon: ArrowDownLeftIcon,
    color: "text-accent",
    bgColor: "bg-accent/10",
  },
  outgoing: {
    icon: ArrowUpRightIcon,
    color: "text-destructive",
    bgColor: "bg-destructive/10",
  },
  self: {
    icon: MinusIcon,
    color: "text-muted-foreground",
    bgColor: "bg-muted",
  },
};

const PureTransactionRow = ({
  transaction,
  onQuickAction,
  userAddress,
  showChain = false,
  enableHoverActions = true,
  size = "md",
  className,
  onClick,
}: TransactionRowProps) => {
  const [isHovered, setIsHovered] = useState(false);

  const direction = getDirection(transaction, userAddress);
  const config = directionConfig[direction];
  const DirectionIcon = config.icon;
  const chain = getChainInfo(transaction.chainId);

  // Generate quick actions for this transaction
  const quickActions: QuickAction[] = [
    {
      label: "Explain",
      icon: <SearchIcon className="size-3" />,
      prompt: promptGenerators.explainTransaction(transaction.hash, chain.name),
    },
    {
      label: "Sender?",
      icon: <UserIcon className="size-3" />,
      prompt: promptGenerators.aboutAddress(
        transaction.from,
        "sent this transaction"
      ),
    },
    {
      label: "Safe?",
      icon: <ShieldIcon className="size-3" />,
      prompt: promptGenerators.checkTransactionSafety(transaction.hash),
    },
  ];

  const sizeClasses = {
    sm: "p-2 text-xs",
    md: "p-3 text-sm",
    lg: "p-4 text-base",
  };

  const iconSizes = {
    sm: "size-4",
    md: "size-5",
    lg: "size-6",
  };

  return (
    <div
      className={cn("group relative", className)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Main row */}
      <div
        className={cn(
          "flex items-center gap-3 rounded-lg border transition-all",
          "hover:border-border hover:bg-muted/30",
          onClick && "cursor-pointer",
          sizeClasses[size]
        )}
        onClick={onClick}
        onKeyDown={(e) => {
          if (onClick && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            onClick();
          }
        }}
        role={onClick ? "button" : undefined}
        tabIndex={onClick ? 0 : undefined}
      >
        {/* Direction indicator */}
        <div
          className={cn(
            "flex items-center justify-center rounded-full p-1.5",
            config.bgColor
          )}
        >
          <DirectionIcon className={cn(iconSizes[size], config.color)} />
        </div>

        {/* Transaction hash */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-mono text-foreground">
              {truncateAddress(transaction.hash, 8, 6)}
            </span>
            {showChain && (
              <ChainBadge chainId={transaction.chainId} size="sm" />
            )}
          </div>
          <div className="flex items-center gap-1.5 text-muted-foreground text-xs">
            <span>{direction === "incoming" ? "from" : "to"}</span>
            <span className="font-mono">
              {truncateAddress(
                direction === "incoming"
                  ? transaction.from
                  : (transaction.to ?? "Contract Creation"),
                4,
                4
              )}
            </span>
          </div>
        </div>

        {/* Method badge */}
        {transaction.method && (
          <MethodBadge method={transaction.method} size="sm" />
        )}

        {/* Value */}
        <ValueDisplay
          className="font-medium"
          decimals={18}
          direction={direction}
          showDirection
          showSign
          size={size === "lg" ? "md" : "sm"}
          symbol={chain.nativeCurrency.symbol}
          value={transaction.value}
        />

        {/* Status */}
        <TransactionStatusBadge size="sm" status={transaction.status} />

        {/* Time */}
        <TimeAgo
          className="min-w-[4rem] text-right"
          size="sm"
          timestamp={transaction.timestamp}
        />

        {/* Hover action hint */}
        {enableHoverActions && (
          <span
            className={cn(
              "ml-auto whitespace-nowrap text-primary text-xs transition-opacity",
              isHovered ? "opacity-100" : "opacity-0"
            )}
          >
            Ask →
          </span>
        )}
      </div>

      {/* Expandable action panel */}
      <AnimatePresence>
        {enableHoverActions && isHovered && (
          <motion.div
            animate={{ opacity: 1, height: "auto" }}
            className="overflow-hidden"
            exit={{ opacity: 0, height: 0 }}
            initial={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.15 }}
          >
            <div className="flex gap-2 rounded-b-lg border-x border-b bg-muted/20 px-3 py-2">
              <InlineActions actions={quickActions} onAction={onQuickAction} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const TransactionRow = memo(PureTransactionRow);

/**
 * Skeleton loader for transaction row
 */
export function TransactionRowSkeleton({
  size = "md",
}: {
  size?: "sm" | "md" | "lg";
}) {
  const sizeClasses = {
    sm: "p-2",
    md: "p-3",
    lg: "p-4",
  };

  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-lg border",
        sizeClasses[size]
      )}
    >
      <div className="size-8 animate-pulse rounded-full bg-muted" />
      <div className="flex-1 space-y-2">
        <div className="h-4 w-32 animate-pulse rounded bg-muted" />
        <div className="h-3 w-24 animate-pulse rounded bg-muted" />
      </div>
      <div className="h-5 w-16 animate-pulse rounded bg-muted" />
      <div className="h-5 w-20 animate-pulse rounded bg-muted" />
      <div className="h-5 w-14 animate-pulse rounded bg-muted" />
      <div className="h-4 w-12 animate-pulse rounded bg-muted" />
    </div>
  );
}
